"use client"

import { useState, useEffect } from "react"
import { Trash2, Edit2, Check, X, ThumbsUp, ThumbsDown, MessageCircle } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useCommentsStore } from "@/lib/comments-store"
import { useAuthStore } from "@/lib/auth-store"
import { LoginPromptDialog } from "@/components/auth/login-prompt-dialog"
import Link from "next/link"
import { useTranslations } from "next-intl"

interface CommentsPanelProps {
  postSite: string
  postId: string
}

export function CommentsPanel({ postSite, postId }: CommentsPanelProps) {
  const t = useTranslations("commentsPanel")
  const { fetchComments, addComment, editComment, deleteComment, voteComment, getComments, isLoading } =
    useCommentsStore()
  const { user, profile, isGuest } = useAuthStore()
  const comments = getComments(postSite, postId)

  const [newComment, setNewComment] = useState("")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [replyingTo, setReplyingTo] = useState<string | null>(null)

  useEffect(() => {
    fetchComments(postSite, postId)
  }, [postSite, postId, fetchComments])

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    if (isGuest || !user) {
      setShowLoginPrompt(true)
      return
    }

    const success = await addComment(postSite, postId, newComment, user.id, replyingTo || undefined)
    if (success) {
      setNewComment("")
      setReplyingTo(null)
    }
  }

  const handleEditStart = (id: string, text: string) => {
    setEditingId(id)
    setEditText(text)
  }

  const handleEditSave = async (id: string) => {
    if (editText.trim()) {
      await editComment(id, editText)
      setEditingId(null)
    }
  }

  const handleVote = async (commentId: string, voteType: "up" | "down") => {
    if (isGuest || !user) {
      setShowLoginPrompt(true)
      return
    }
    await voteComment(commentId, voteType)
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-2 px-4">
        <Input
          placeholder={isGuest ? t("loginToComment") : t("addAComment")}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
          className="bg-secondary/20 border-secondary/40 text-foreground placeholder:text-muted-foreground/50"
          disabled={isLoading}
        />
        <Button
          onClick={handleAddComment}
          disabled={!newComment.trim() || isLoading}
          size="sm"
          className="bg-primary/80 hover:bg-primary text-primary-foreground"
        >
          {replyingTo ? t("reply") : t("add")}
        </Button>
      </div>

      {replyingTo && (
        <div className="px-4 mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <MessageCircle className="h-3 w-3" />
          <span>{t("replyingToComment")}</span>
          <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => setReplyingTo(null)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {isLoading && comments.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">{t("loadingComments")}</div>
      ) : comments.length > 0 ? (
        <ScrollArea className="px-4 mt-3 h-full">
          <div className="space-y-3">
            {comments.map((comment) => {
              const isOwner = user?.id === comment.user_id

              return (
                <div
                  key={comment.id}
                  className={`bg-secondary/20 border border-secondary/40 rounded-lg p-3 space-y-2 backdrop-blur-sm ${
                    comment.parent_id ? "ml-6 border-l-2 border-l-primary/30" : ""
                  }`}
                >
                  {editingId === comment.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="bg-secondary/40 border-secondary/60 text-foreground"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditSave(comment.id)}
                          className="h-7 bg-green-500/20 hover:bg-green-500/30 border-green-500/40 text-green-400"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingId(null)}
                          className="h-7 bg-red-500/20 hover:bg-red-500/30 border-red-500/40 text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <Link href={`/user/${comment.user_id}`}>
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={comment.profile_picture_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {comment.username?.[0]?.toUpperCase() || "?"}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Link
                              href={`/user/${comment.user_id}`}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              {comment.username || "Anonymous"}
                            </Link>
                            <span className="text-xs text-muted-foreground">
                              {formatTime(comment.created_at)}
                              {comment.is_edited && <span className="ml-1">{t("edited")}</span>}
                            </span>
                          </div>
                          <p className="text-sm text-foreground mt-1">{comment.content}</p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReplyingTo(comment.id)}
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <MessageCircle className="h-3 w-3 mr-1" />
                          {t("reply")}
                        </Button>

                        <div className="flex gap-1 items-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleVote(comment.id, "up")}
                            className="h-6 px-1.5 text-muted-foreground hover:text-green-400 hover:bg-green-500/20"
                          >
                            <ThumbsUp className="h-3 w-3" />
                            {comment.upvotes > 0 && <span className="text-xs ml-1">{comment.upvotes}</span>}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleVote(comment.id, "down")}
                            className="h-6 px-1.5 text-muted-foreground hover:text-red-400 hover:bg-red-500/20"
                          >
                            <ThumbsDown className="h-3 w-3" />
                            {comment.downvotes > 0 && <span className="text-xs ml-1">{comment.downvotes}</span>}
                          </Button>

                          {isOwner && (
                            <>
                              <div className="w-px h-4 bg-border mx-1" />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditStart(comment.id, comment.content)}
                                className="h-6 w-6 p-0 text-blue-400 hover:bg-blue-500/20"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteComment(comment.id)}
                                className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/20"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <MessageCircle className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">{t("noCommentsYet")}</p>
          <p className="text-xs">{t("beTheFirstToComment")}</p>
        </div>
      )}

      <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} feature="comment on posts" />
    </div>
  )
}
